import { Box, Typography, CircularProgress, TextField, Button, Stack, Divider } from '@mui/material';
import React, { useState } from 'react';
import { BffBrandType } from '@/types/generated';
import { isValidZipCode } from './SchedulingWidget.helpers';
import { widgetContainerStyles, headingStyles, zipCodeInputContainerStyles, zipCodeHeadingStyles, zipCodeHelperTextStyles, zipCodeFormContainerStyles, zipCodeInputFieldStyles, searchButtonStyles, dividerStyles, continueButtonStyles, loadingContainerStyles } from './SchedulingWidget.styles';
import { FacilitySearchResult, GeocodingResponse } from './SchedulingWidget.types';
import { useAppContext } from '@shared-components/context';
import { getBffNearByFacilitiesByLocation } from '@shared-components/services/getBffNearByFacilitiesByLocation';
import { logError } from '@utils/miscUtils';
export interface ZipCodePickerProps {
  scheduleConsultationUrl: string;
  onFacilityFound: (facility: FacilitySearchResult, allFacilities: FacilitySearchResult[]) => void;
  brand: BffBrandType;
}
export default function ZipCodePicker({
  scheduleConsultationUrl,
  onFacilityFound,
  brand
}: ZipCodePickerProps) {
  const {
    environment,
    config,
    appName,
    appVersion
  } = useAppContext();
  const [zipCode, setZipCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const handleSearch = async () => {
    if (!isValidZipCode(zipCode)) {
      setError('Please enter a valid zip code');
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const geocodeResponse = await fetch(`https://api.zippopotam.us/us/${zipCode}`);
      if (!geocodeResponse.ok) {
        setError('Invalid ZIP code');
        setIsLoading(false);
        return;
      }
      const geocodeData = (await geocodeResponse.json()) as GeocodingResponse;
      const latitude = Number.parseFloat(geocodeData.places[0].latitude);
      const longitude = Number.parseFloat(geocodeData.places[0].longitude);
      const bffGraphqlUrl = environment === 'prod' ? config.services.prod.BEFFE_GRAPHQL_URL : config.services.nonprod.BEFFE_GRAPHQL_URL;
      if (!bffGraphqlUrl) {
        setError('API configuration error');
        setIsLoading(false);
        return;
      }
      const response = await getBffNearByFacilitiesByLocation(brand, latitude, longitude, bffGraphqlUrl, appName, appVersion, {
        radiusMiles: 200,
        maxItems: 10,
        fetchAllIfNoResults: false
      });
      if (response === null) {
        setError('Failed to search. Please try again.');
        setIsLoading(false);
        return;
      }
      const facilities = response?.facilityByLocation?.filter(f => f !== null) || [];
      if (facilities.length > 0) {
        const closestFacility = facilities[0];
        const facility: FacilitySearchResult = {
          id: closestFacility?.code ?? '',
          name: closestFacility?.displayName || closestFacility?.name || '',
          address: closestFacility?.address?.address1 ?? '',
          city: closestFacility?.address?.city ?? '',
          state: closestFacility?.address?.stateCode ?? '',
          zipCode: closestFacility?.address?.zipCode ?? '',
          distance: closestFacility?.location?.distance ?? undefined,
          phoneNumber: closestFacility?.phoneNumber ?? undefined
        };
        const allFacilities: FacilitySearchResult[] = facilities.map(f => ({
          id: f?.code ?? '',
          name: f?.displayName || f?.name || '',
          address: f?.address?.address1 ?? '',
          city: f?.address?.city ?? '',
          state: f?.address?.stateCode ?? '',
          zipCode: f?.address?.zipCode ?? '',
          distance: f?.location?.distance ?? undefined,
          phoneNumber: f?.phoneNumber ?? undefined
        }));
        onFacilityFound(facility, allFacilities);
      } else {
        setError('No facilities found near this location');
      }
    } catch (err) {
      logError(err instanceof Error ? err.message : String(err));
      setError('Failed to search. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };
  if (isLoading) {
    return <Box sx={widgetContainerStyles} data-testid="scheduling-widget-loading">
				<Box sx={loadingContainerStyles}>
					<CircularProgress />
				</Box>
			</Box>;
  }
  return <Box sx={widgetContainerStyles} data-testid="scheduling-widget-no-location" data-sentry-element="Box" data-sentry-component="ZipCodePicker" data-sentry-source-file="ZipCodePicker.tsx">
			<Typography sx={headingStyles} data-sentry-element="Typography" data-sentry-source-file="ZipCodePicker.tsx">Schedule a consultation</Typography>

			<Box sx={zipCodeInputContainerStyles} data-sentry-element="Box" data-sentry-source-file="ZipCodePicker.tsx">
				<Stack gap={0.5} sx={{
        flex: 1
      }} data-sentry-element="Stack" data-sentry-source-file="ZipCodePicker.tsx">
					<Typography sx={zipCodeHeadingStyles} data-sentry-element="Typography" data-sentry-source-file="ZipCodePicker.tsx">Let&apos;s find your nearest center</Typography>
					<Typography variant="bodyMediumBook" sx={zipCodeHelperTextStyles} data-sentry-element="Typography" data-sentry-source-file="ZipCodePicker.tsx">
						Please enter your ZIP code.
					</Typography>
				</Stack>
				<Box sx={zipCodeFormContainerStyles} data-sentry-element="Box" data-sentry-source-file="ZipCodePicker.tsx">
					<Stack direction={{
          xs: 'column',
          sm: 'row'
        }} gap={2} alignItems="center" flexWrap="wrap" data-sentry-element="Stack" data-sentry-source-file="ZipCodePicker.tsx">
						<TextField label="ZIP code*" value={zipCode} onChange={e => {
            const value = e.target.value;
            if (/^\d{0,5}$/.test(value)) {
              setZipCode(value);
              setError(null);
            }
          }} onKeyPress={e => {
            if (e.key === 'Enter') {
              void handleSearch();
            } else if (!/\d/.test(e.key)) {
              e.preventDefault();
            }
          }} error={!!error} helperText={error} disabled={isLoading} data-testid="zip-code-input" sx={zipCodeInputFieldStyles} inputProps={{
            inputMode: 'numeric',
            pattern: '[0-9]*',
            maxLength: 5
          }} data-sentry-element="TextField" data-sentry-source-file="ZipCodePicker.tsx" />
						<Button variant="contained" onClick={() => void handleSearch()} disabled={isLoading || !zipCode} sx={searchButtonStyles} data-testid="search-button" data-sentry-element="Button" data-sentry-source-file="ZipCodePicker.tsx">
							Search
						</Button>
					</Stack>
				</Box>
			</Box>

			<Divider sx={dividerStyles} data-sentry-element="Divider" data-sentry-source-file="ZipCodePicker.tsx" />

			<Button variant="outlined" href={scheduleConsultationUrl} sx={continueButtonStyles} data-testid="continue-to-scheduling-button" data-sentry-element="Button" data-sentry-source-file="ZipCodePicker.tsx">
				Continue to scheduling
			</Button>
		</Box>;
}